import i18n, { Locale, SUPPORTED_LOCALES } from '~app/i18n';

// Query params that select the UI language on an entry URL. `language` is the
// canonical one; `lang` is accepted as an alias because external link builders
// (e.g. the DialogShift chatbot) use it.
export const URL_LOCALE_PARAMS = ['language', 'lang'] as const;

const toSupportedLocale = (raw: string | null): Locale | undefined =>
  SUPPORTED_LOCALES.find(locale => locale === raw?.trim().toLowerCase());

// `language` wins whenever it is present; `lang` is only read when `language`
// is missing. Unsupported values yield undefined.
export const getUrlLocale = (
  searchParams: URLSearchParams,
): Locale | undefined =>
  searchParams.has('language')
    ? toSupportedLocale(searchParams.get('language'))
    : toSupportedLocale(searchParams.get('lang'));

// For entry loaders that redirect without the query string: useUrlLocale only
// sees the URL after the redirect, so the loader applies the language itself,
// before its own API requests pick a locale.
export const applyUrlLocale = async (
  searchParams: URLSearchParams,
): Promise<void> => {
  const locale = getUrlLocale(searchParams);
  if (locale && locale !== i18n.language) {
    await i18n.changeLanguage(locale);
  }
};

const REDIRECTING_ENTRY_PATHS = ['/search', '/add-room'];
const SHARED_SESSION_ENTRY_PATHS = ['', '/booking'];

// Entry URLs whose loader applies the URL language itself and then redirects
// without the query string: /search/, /add-room/, and / or /booking carrying a
// sessionId. useUrlLocale must not navigate on these. Its navigate would abort
// the pending loader, whose side effects (cart, session) still run, and start
// it a second time on the stripped URL.
export const isLocaleAppliedByLoader = (
  pathname: string,
  searchParams: URLSearchParams,
): boolean => {
  const path = pathname.replace(/\/+$/, '');
  if (REDIRECTING_ENTRY_PATHS.includes(path)) return true;
  return (
    SHARED_SESSION_ENTRY_PATHS.includes(path) &&
    Boolean(searchParams.get('sessionId')?.trim())
  );
};
